import { useEffect, lazy, Suspense } from "react";
import Navbar from "./components/Navbar/Navbar.jsx";
import { Routes, Route, useLocation } from "react-router-dom";
import Footer from "./components/footer/Footer.jsx";
import "./App.css";
import ScrollToTop from "./components/ScrollToTop.jsx";
import RequireAuth from "./auth/RequiredAuth.jsx";
import Breadcrumbs from "./components/Breadcrumbs/Breadcrumbs.jsx";
import Spinner from "./components/Spinners/Spinner";

// ── Lazy-loaded route components (code-split per page) ──────────────
const Home = lazy(() => import("./pages/Home/Home.jsx"));
const SavingsPage = lazy(() => import("./SavingsPage/SavingsPage"));
const FDPage = lazy(() => import("./FDPage/FDPage"));
const FCNRPage = lazy(() => import("./FCNRPage/FCNRPage"));
const FcnrBankDetails = lazy(() => import("./FCNRPage/bankDetails/FcnrBankDetails"));
const ContactUs = lazy(() => import("./pages/ContactUsPage.jsx/ContactUs.jsx"));
const BankDetails = lazy(() => import("./FDPage/bankDetails/BankDetails.jsx"));
const BankFullDetails = lazy(() => import("./FDPage/bankDetails/BankFullDetails.jsx"));
const BankRateDetails = lazy(() => import("./SavingsPage/bankDetailsSavings/BankRateDetails.jsx"));
const Privacy = lazy(() => import("./pages/privacy/Privacy.jsx"));
const TermsOfService = lazy(() => import("./pages/T&C/TermsOfService"));
const CreditCardTable = lazy(() => import("./CreditCards/CreditCardTable.jsx"));
const FuelCardsLanding = lazy(() => import("./FuelCards/FuelCardsLanding.jsx"));
const FuelCardDetails = lazy(() => import("./FuelCards/FuelCardDetails.jsx"));
const BlogLanding = lazy(() => import("./components/blog/BlogLanding.jsx"));
const BlogSinglePost = lazy(() => import("./components/blog/BlogSinglePost.jsx"));
const SignupForm = lazy(() => import("./auth/SignUp.jsx"));
const LoginForm = lazy(() => import("./auth/SignIn.jsx"));
const PendingApproval = lazy(() => import("./auth/PendingApproval.jsx"));
const NotFoundPage = lazy(() => import("./pages/404page/NotFoundPage.jsx"));
const VikalpFDCalculator = lazy(() => import("./components/fd_calculator/FDCalculator.jsx"));
const FeedList = lazy(() => import("./pages/Feeds/FeedsLanding.jsx"));
const ComparePage = lazy(() => import("./pages/pSEO/FDComparePage.jsx"));
const RankingPage = lazy(() => import("./pages/pSEO/RankingPage.jsx"));
const TenorRankingPage = lazy(() => import("./pages/pSEO/TenorRankingPage.jsx"));
const SingleBankRatesPage = lazy(() => import("./pages/pSEO/SingleBankRatesPage.jsx"));
const FDCalculatorPage = lazy(() => import("./pages/pSEO/FDCalculatorPage.jsx"));
const SDCalculatorPage = lazy(() => import("./pages/pSEO/SDCalculatorPage.jsx"));
const SDComparePage = lazy(() => import("./pages/pSEO/SDComparePage.jsx"));

function App() {
  const location = useLocation();
  // useEffect(() => {
  //   setupAxiosInterceptors();
  // }, []);

  // Uncomment later.
  // useEffect(() => {
  //   const tokens = localStorage.getItem("tokens");
  //   if (tokens && !user) {
  //     dispatch(getUserDetails());
  //   }
  // }, [dispatch, user]);

  // useEffect(() => {
  //   const auth = async () => await dispatch(checkAuthStatus());
  // }, []);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    window.addEventListener("resize", setVh);
    setVh();

    return () => window.removeEventListener("resize", setVh);
  }, []);

  // useEffect(() => {
  //   dispatch(fetchSavings());
  //   dispatch(fetchFdRates());
  // }, [dispatch]);

  // useEffect(() => {
  //   InitializeGoogleAnalytics();
  //   ReactGA.send({
  //     hitType: "pageview",
  //     page: window.location.pathname,
  //     title: "Home Page",
  //   });
  // }, []);

  // check why its used.
  // useEffect(() => {
  //   gapi.load("client:auth2", () => {
  //     gapi.auth2
  //       .init({
  //         client_id:
  //           "1049121200498-mnfho130h5k9j0lkeosses63vq9fra6r.apps.googleusercontent.com",
  //       })
  //       .then(() => {
  //         gapi.auth2.getAuthInstance().isSignedIn.listen((isSignedIn) => {
  //           if (isSignedIn) {
  //             ReactGA.send({
  //               hitType: "pageview",
  //               page: window.location.pathname,
  //               title: "Home Page",
  //             });
  //           }
  //         });
  //       });
  //   });
  // }, []);

  const giveColor = () => {
    if (
      location.pathname === "/fixed-deposit-rates" ||
      location.pathname === "/fcnr-deposit-rates" ||
      location.pathname === "/savings-account-interest-rates" ||
      location.pathname === "/signin" ||
      location.pathname === "/signup"
    ) {
      return "#f8f9fa";
    } else if (location.pathname === "/contactUs") {
      return "#f1f3f4";
    } else {
      return "#ffffff";
    }
  };

  return (
    <div className="app">
      <ScrollToTop />
      <Navbar />
      {location.pathname !== "/" &&
        !location.pathname.startsWith("/savings-account-interest-rates/") &&
        !location.pathname.startsWith("/fixed-deposit-rates/") &&
        !location.pathname.startsWith("/small-savings/") &&
        !location.pathname.startsWith("/fcnr-deposit-rates/") &&
        !location.pathname.startsWith("/bank-details/") &&
        !location.pathname.startsWith("/blog/") && (
          <Breadcrumbs color={giveColor()} />
        )}
      <div style={{ flexGrow: 1 }}>
        <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/signup" element={<SignupForm />} />
          <Route path="/signin" element={<LoginForm />} />
          <Route path="/pending" element={<PendingApproval />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<Home />} />
            <Route path="/credit-cards" element={<CreditCardTable />} />
            <Route path="/fuel-credit-cards" element={<FuelCardsLanding />} />
            <Route path="/fuel-credit-cards/:ccid" element={<FuelCardDetails />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="feedback" element={<ContactUs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms-and-condition" element={<TermsOfService />} />
            <Route
              path="savings-account-interest-rates"
              element={<SavingsPage />}
            />
            <Route
              path="/savings-account-interest-rates/:id"
              element={<BankRateDetails />}
            />
             <Route path="fixed-deposit-rates" element={<FDPage />} />
             <Route path="/fixed-deposit-rates/:id" element={<BankDetails />} />
             <Route path="/small-savings/:id" element={<BankDetails />} />
            <Route path="/bank-details/:id" element={<BankFullDetails />} />
            <Route path="fcnr-deposit-rates" element={<FCNRPage />} />
            <Route path="/fcnr-deposit-rates/:id" element={<FcnrBankDetails />} />
            <Route path="/blog" element={<BlogLanding />} />
            <Route path="/blog/:id" element={<BlogSinglePost />} />
            <Route path="/fd-calculator" element={<VikalpFDCalculator />} />
            <Route path="/rate-tracker" element={<FeedList />} />

            <Route path="/compare/:slug" element={<ComparePage />} />
            <Route path="/fd-rates/:slug" element={<RankingPage />} />
            <Route path="/fd-tenor-rates/:slug" element={<TenorRankingPage />} />
            <Route path="/bank-fd-rates/:slug" element={<SingleBankRatesPage />} />
            <Route path="/fd-calculator/:slug" element={<FDCalculatorPage />} />
            <Route path="/sd-calculator/:slug" element={<SDCalculatorPage />} />
            <Route path="/compare-savings/:slug" element={<SDComparePage />} />

            {/* <Route path="/content" element={<Content />} /> */}
            {/* Catch-all: redirect unknown paths for unauthenticated users */}
            {/* <Route path="*" element={<Navigate to="/signin" replace />} /> */}
            {/* <Route path="/blog" element={<BlogLanding />} />

          // https://chatgpt.com/c/6858e441-0ec8-800d-b769-89379e609275 check this before using blog, it has some issue discusion about apache and nginx conflict in the digital ocean droplet server.
        <Route path="/blog/:id" element={<BlogSinglePost />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/select-banks" element={<SelectBanks />} />
        <Route
          path="/forgot-password/:uid/:token"
          element={<ResetPassword />}
        />
        <Route path="/verify-email/:token" element={<Verifyemail />} />
        <Route
          path="/user-account"
          element={<PrivateRoute children={<UserDetails />} />}
        />
        <Route path="/user-account" element={<UserDetails />} />
        <Route path="/forgot-password/" element={<RequestPassword />} />
        <Route path="/product-list/" element={<ProductList />} /> */}
            {/* <Route path="/test/" element={<EmailInput />} /> */}
            {/* test routes */}
            {/* <Route path="test" element={<BlogLanding />} /> */}
            {/* <Route path="*" element={<PageNotFound />} /> */}
          </Route>
        </Routes>
        </Suspense>
      </div>

      {/* <div className="footer"> */}
      <Footer />
      {/* </div> */}

      {/* <Test /> */}
    </div>
  );
}

export default App;
